<template>
  <Card :border="border" :radius="radius" class="px-6 py-7 text-center">
    <Icon name="lock-solid" class="mx-auto mb-3 w-6 text-gray-400" />
    <h3 class="text-lg font-semibold text-gray-900">
      {{ heading }}
    </h3>
    <p class="mx-auto mt-2 max-w-xl text-sm text-gray-600">
      {{ body }}
    </p>

    <div
      v-if="$slots.notice"
      class="mx-auto mt-4 flex max-w-xl items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-left text-sm text-amber-800"
    >
      <Icon name="exclamation-circle-solid" class="mt-0.5 w-4 shrink-0" />
      <span><slot name="notice" /></span>
    </div>

    <!-- The terms the manager is being asked to accept. Consent is a legal
         act on the organization's behalf, so the full text has to be on the
         page that records it — not only in the settings modal. -->
    <!-- isPending, not isLoading: the query waits for the profile behind
         useOrganizationScopeReady, and a gated query is not fetching. -->
    <div v-if="consent.isPending" class="mt-5 flex justify-center">
      <Icon
        name="spinner-third-duotone"
        class="size-6 animate-spin text-gray-400"
      />
    </div>

    <div
      v-else-if="!consentDetail"
      class="mx-auto mt-5 flex max-w-xl flex-col items-center gap-3"
    >
      <p class="text-sm text-red-600">
        The consent terms are unavailable. They have to be shown before consent
        can be recorded.
      </p>
      <button type="button" class="btn btn-outline" @click="consent.refetch()">
        Retry
      </button>
    </div>

    <template v-else>
      <!-- Left-aligned inside the otherwise centred wall: terms read as a
           document. -->
      <div class="mx-auto mt-6 max-w-xl text-left">
        <p
          :id="consentTextLabelId"
          class="mb-1.5 text-xs font-medium tracking-wide text-gray-500"
        >
          Full consent text
        </p>
        <!-- Focusable so the terms can be scrolled without a pointer; they
             run well past this box and are being accepted on the org's
             behalf. -->
        <div
          tabindex="0"
          role="region"
          :aria-labelledby="consentTextLabelId"
          class="max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3.5 text-left text-[13px] leading-relaxed text-gray-600 focus-visible:ring-2 focus-visible:ring-purple-700 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <pre class="font-sans whitespace-pre-wrap">{{
            consentDetail.consentFullText
          }}</pre>
        </div>
      </div>

      <!-- A personal override can unlock the feature for one user while the
           organization is not entitled to it, and consent is the
           organization's to give: `POST /give/` refuses, so the button would
           be a dead end. This outranks the role branch — no administrator can
           accept here either, so nobody may be sent to ask one. -->
      <p v-if="!isConsentable" class="mt-5 text-sm text-gray-500">
        This feature is enabled for your account only. Consent is recorded for
        the whole organization, so it can't be accepted until the feature is
        part of your plan.
      </p>
      <!-- Entitled organization, so a manager really can accept: everyone
           else gets told who to ask rather than a button that 403s. -->
      <!-- isMember, not !isManager: the raw flag reads false while the
           profile loads too, flashing this at managers who can in fact
           accept. v-else-if, not v-if: the isConsentable branch above
           outranks this one and must stay a chain. -->
      <p v-else-if="isMember" class="mt-5 text-sm text-gray-500">
        Ask an administrator in your organization to enable this feature.
      </p>
      <!-- Loading and error get their own branches rather than falling into
           the action below. Consent is a POST the backend gates on IsManager,
           so offering the button before the role is known invites a click
           that 403s — and on a failed profile it would sit there
           indefinitely. -->
      <div v-else-if="roleIsLoading" class="mt-5 flex justify-center">
        <Icon
          name="spinner-third-duotone"
          class="size-6 animate-spin text-gray-400"
        />
      </div>
      <div
        v-else-if="roleFailed"
        class="mx-auto mt-5 flex max-w-xl flex-col items-center gap-3"
      >
        <p class="text-sm text-red-600">
          Could not confirm your permissions, so consent cannot be recorded from
          here yet.
        </p>
        <button type="button" class="btn btn-outline" @click="retryRole()">
          Retry
        </button>
      </div>
      <template v-else>
        <div class="mt-5 flex justify-center">
          <button
            type="button"
            class="btn-primary"
            :disabled="giveConsent.isPending"
            @click="enable"
          >
            {{ giveConsent.isPending ? "Enabling…" : ctaLabel }}
          </button>
        </div>
        <p v-if="giveConsent.isError" class="mt-3 text-sm text-red-600">
          {{ enableError }}
        </p>
      </template>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed, useId, type PropType } from "vue";
import Card from "@/components/Core/Card.vue";
import type { CardRadius } from "@/components/Core/Card.vue";
import Icon from "@/components/Core/Icon.vue";
import {
  useConsentDetailQuery,
  useGiveConsentMutation,
} from "@/services/consent";
import { useOrganizationRole } from "@/services/user";
import { PAYWALL_CONTENT } from "@/services/paywall_content";
import { formatDrfRefusal } from "@/utils/drf-errors";

const props = defineProps({
  border: { type: Boolean, default: true },
  // Matches the radius of whatever the wall renders beside.
  radius: { type: String as PropType<CardRadius>, default: "md" },
  // Backend consent key (snake_case) — the consent endpoints key on the flag's
  // stored key, unlike the flags API which renders camelCase.
  featureKey: { type: String, required: true },
  // camelCase flag key, for the shared sales copy's feature label.
  flag: { type: String, required: true },
});

const consentTextLabelId = useId();

const consent = useConsentDetailQuery(() => props.featureKey);
const giveConsent = useGiveConsentMutation();
const role = useOrganizationRole();
// Destructured for the template: `role.isMember` in markup would be the ref
// object itself — always truthy — because `role` is a plain object of refs.
const { isMember, isLoading: roleIsLoading, isError: roleFailed } = role;
const retryRole = role.refetch;

// Gates the accept button on terms actually being on screen. Covers both a
// failed fetch and a 200 whose text is blank: either way a manager must not be
// able to accept text they were never shown.
const consentDetail = computed(() =>
  consent.data?.consentFullText ? consent.data : undefined,
);

// The organization's own entitlement, not `isAvailable`: the second includes
// the caller's personal override, which cannot authorize a write on the
// organization's behalf.
const isConsentable = computed(() => consent.data?.isConsentable === true);

const enableError = computed(() =>
  formatDrfRefusal(giveConsent.error, "Could not enable. Please try again."),
);

const featureLabel = computed(
  () => PAYWALL_CONTENT[props.flag]?.label ?? consent.data?.featureName ?? "",
);

// A paying org whose consent went stale because the terms changed must not be
// addressed as if it had never consented — that reads as losing access.
const isReconsent = computed(() => consent.data?.requiresReconsent === true);

const heading = computed(() =>
  isReconsent.value
    ? "Terms updated — please review"
    : (consent.data?.consentTitle ?? "Consent required"),
);

const body = computed(() => {
  if (isReconsent.value) {
    return `The terms for ${featureLabel.value} have changed since your organization accepted them. Review and accept to continue using it.`;
  }
  return (
    consent.data?.consentDescription ??
    `${featureLabel.value} needs your organization's consent before it can be used.`
  );
});

const ctaLabel = computed(() =>
  isReconsent.value ? "Accept updated terms" : `Enable ${featureLabel.value}`,
);

function enable(): void {
  giveConsent.mutate(props.featureKey);
}
</script>
